import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';
import type { TabDef } from '../../components/ui/tabs';
import { money } from '../../format';
import './event-gallery';

const statusLabels = (): Record<string, string> => ({
  draft: t('events.status.draft'), published: t('events.status.published'),
  completed: t('events.status.completed'), cancelled: t('events.status.cancelled'),
});
const partLabels = (): Record<string, string> => ({
  interested: t('events.status.participant.interested'),
  confirmed: t('events.status.participant.confirmed'),
  denied: t('events.status.participant.denied'),
});
const todayStr = () => new Date().toISOString().slice(0, 10);

@customElement('cadence-event-detail-page')
export class CadenceEventDetailPage extends LitElement {
  @property() eventId = '';
  @state() private ev: any = null;
  @state() private types: any[] = [];
  @state() private tags: any[] = [];
  @state() private participants: any[] = [];
  @state() private expenses: any[] = [];
  @state() private revenue: any[] = [];
  @state() private financials: any = null;
  @state() private allStudents: any[] = [];
  @state() private tab = 'overview';
  @state() private loading = true;

  @state() private addStudentId = '';
  @state() private expForm: any = { description: '', amount: '', currency: '', date: todayStr() };
  @state() private revForm: any = { description: '', amount: '', currency: '', date: todayStr() };
  @state() private fees: any[] = [];
  @state() private feeForm: any = { name: '', amount: '', currency: '' };
  @state() private billChoice: Record<string, string> = {};
  @state() private showEdit = false;
  @state() private editValue: any = {};
  @state() private savingEdit = false;

  static styles = css`
    :host { display: block; }
    .back { color: var(--color-primary); text-decoration: none; font-size: 0.88rem; }
    .head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin: var(--space-3) 0 4px; }
    h1 { margin: 0; font-size: 1.5rem; }
    .sub { color: var(--color-text-secondary); margin: 0 0 var(--space-4); }
    .pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 0.78rem; font-weight: 600; vertical-align: middle; margin-left: 8px; }
    .s-draft { background: var(--color-bg-secondary); color: var(--color-text-secondary); }
    .s-published { background: #dcfce7; color: #166534; }
    .s-completed { background: #dbeafe; color: #1e40af; }
    .s-cancelled { background: #fee2e2; color: #991b1b; }
    .panel { margin-top: var(--space-4); }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--space-4); margin-bottom: var(--space-4); }
    .field .k { color: var(--color-text-secondary); font-size: 0.8rem; }
    .field .v { font-weight: 600; }
    .fin { display: flex; gap: var(--space-5); flex-wrap: wrap; }
    .fin .amt { font-size: 1.3rem; font-weight: 700; }
    .pos { color: var(--color-success, #16a34a); }
    .neg { color: var(--color-danger); }
    .row { display: flex; gap: 10px; align-items: flex-end; margin-bottom: var(--space-3); flex-wrap: wrap; }
    .item { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--color-border); }
    .item:last-child { border-bottom: none; }
    .item .meta { color: var(--color-text-secondary); font-size: 0.82rem; }
    .acts { display: flex; gap: 14px; align-items: center; }
    .acts > span { white-space: nowrap; font-weight: 600; }
    .agreed { color: var(--color-success, #16a34a); font-weight: 600; }
    .md-card { border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); margin-top: var(--space-4); }
    .md-card > summary { cursor: pointer; padding: var(--space-3) var(--space-4); font-weight: 600; list-style: none; display: flex; align-items: center; gap: 8px; user-select: none; }
    .md-card > summary::-webkit-details-marker { display: none; }
    .md-card > summary::before { content: '▸'; color: var(--color-text-secondary); transition: transform 0.15s; }
    .md-card[open] > summary::before { transform: rotate(90deg); }
    .md-card .fn { color: var(--color-text-secondary); font-weight: 400; font-size: 0.85rem; }
    .md-card cadence-markdown-renderer { display: block; padding: 0 var(--space-4) var(--space-4); }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }
  updated(c: Map<string, unknown>) { if (c.has('eventId') && this.eventId) this.load(); }

  private async load() {
    if (!this.eventId) return;
    this.loading = true;
    try {
      const [ev, types, tags, participants, fees, expenses, revenue, financials, students] = await Promise.all([
        api.get<any>(`/events/${this.eventId}`),
        api.get<any[]>('/events/types'),
        api.get<any[]>('/tags'),
        api.get<any[]>(`/events/${this.eventId}/participants`),
        api.get<any[]>(`/events/${this.eventId}/fees`),
        api.get<any[]>(`/events/${this.eventId}/expenses`),
        api.get<any[]>(`/events/${this.eventId}/revenue`),
        api.get<any>(`/events/${this.eventId}/financials`),
        api.get<any>('/students?limit=200'),
      ]);
      this.ev = ev; this.types = types; this.tags = tags; this.participants = participants; this.fees = fees;
      this.expenses = expenses; this.revenue = revenue; this.financials = financials;
      this.allStudents = students.students ?? [];
      const defCur = fees[0]?.currency ?? '';
      if (!this.expForm.currency) this.expForm = { ...this.expForm, currency: defCur };
      if (!this.revForm.currency) this.revForm = { ...this.revForm, currency: defCur };
      if (!this.feeForm.currency) this.feeForm = { ...this.feeForm, currency: defCur };
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private async patchEvent(patch: any) {
    try { this.ev = await api.put<any>(`/events/${this.eventId}`, patch); showToast(t('events.toast.eventUpdated'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  private openEdit() {
    const e = this.ev;
    this.editValue = {
      name: e.name, type_id: e.type_id ?? '', status: e.status,
      start_date: e.start_date ?? '', end_date: e.end_date ?? '',
      capacity: e.capacity ?? '', description: e.description ?? '',
      visibility: e.visibility ?? 'all', tag_ids: e.visibility_tag_ids ?? [],
      details_md: e.details_md ?? null, details_filename: e.details_filename ?? null,
    };
    this.showEdit = true;
  }
  private async saveEdit(v: any) {
    this.savingEdit = true;
    await this.patchEvent({
      ...v,
      capacity: v.capacity === '' ? null : Number(v.capacity),
    });
    this.savingEdit = false;
    this.showEdit = false;
    await this.load();
  }

  // Participants
  private async addParticipant() {
    if (!this.addStudentId) return;
    try { await api.post(`/events/${this.eventId}/participants`, { student_id: this.addStudentId }); this.addStudentId = ''; await this.load(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async setStatus(studentId: string, status: string) {
    try { await api.patch(`/events/${this.eventId}/participants/${studentId}`, { status }); await this.load(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async removeParticipant(studentId: string) {
    try { await api.del(`/events/${this.eventId}/participants/${studentId}`); await this.load(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async billFee(studentId: string) {
    const feeId = this.billChoice[studentId] ?? this.fees[0]?.id;
    if (!feeId) { showToast(t('events.toast.addFeeFirst'), 'error'); return; }
    try { await api.post(`/events/${this.eventId}/participants/${studentId}/fee`, { event_fee_id: feeId }); showToast(t('events.toast.feeAddedToStudent'), 'success'); await this.load(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  // Fee options
  private async addFee() {
    const f = this.feeForm;
    if (!f.name.trim() || !(Number(f.amount) > 0) || !f.currency) { showToast(t('events.toast.feeFieldsRequired'), 'error'); return; }
    try { await api.post(`/events/${this.eventId}/fees`, { ...f, amount: Number(f.amount) }); this.feeForm = { name: '', amount: '', currency: f.currency }; await this.load(); showToast(t('events.toast.feeOptionAdded'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async delFee(id: string) { try { await api.del(`/events/${this.eventId}/fees/${id}`); await this.load(); } catch (e) { showToast((e as Error).message, 'error'); } }

  // Expenses / revenue
  private async addExpense() {
    const f = this.expForm;
    if (!f.description.trim() || !(Number(f.amount) > 0) || !f.currency) { showToast(t('events.toast.entryFieldsRequired'), 'error'); return; }
    try { await api.post(`/events/${this.eventId}/expenses`, { ...f, amount: Number(f.amount) }); this.expForm = { description: '', amount: '', currency: f.currency, date: todayStr() }; await this.load(); showToast(t('events.toast.expenseAdded'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async delExpense(id: string) { try { await api.del(`/events/${this.eventId}/expenses/${id}`); await this.load(); } catch (e) { showToast((e as Error).message, 'error'); } }
  private async addRevenue() {
    const f = this.revForm;
    if (!f.description.trim() || !(Number(f.amount) > 0) || !f.currency) { showToast(t('events.toast.entryFieldsRequired'), 'error'); return; }
    try { await api.post(`/events/${this.eventId}/revenue`, { ...f, amount: Number(f.amount) }); this.revForm = { description: '', amount: '', currency: f.currency, date: todayStr() }; await this.load(); showToast(t('events.toast.revenueAdded'), 'success'); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async delRevenue(id: string) { try { await api.del(`/events/${this.eventId}/revenue/${id}`); await this.load(); } catch (e) { showToast((e as Error).message, 'error'); } }

  private get tabs(): TabDef[] {
    return [
      { id: 'overview', label: t('events.tabs.overview') },
      { id: 'details', label: t('events.tabs.details') },
      { id: 'participants', label: t('events.tabs.participants', { count: this.participants.length }) },
      { id: 'fees', label: t('events.tabs.fees', { count: this.fees.length }) },
      { id: 'expenses', label: t('events.tabs.expenses') },
      { id: 'revenue', label: t('events.tabs.revenue') },
      { id: 'gallery', label: t('events.tabs.gallery') },
      { id: 'todos', label: t('events.tabs.todos') },
      { id: 'notes', label: t('events.tabs.notes') },
      { id: 'files', label: t('events.tabs.files') },
    ];
  }

  render() {
    if (this.loading || !this.ev) return html`<cadence-loading></cadence-loading>`;
    const e = this.ev;
    return html`
      <a class="back" href="/admin/events" data-link>← ${t('events.detail.back')}</a>
      <div class="head">
        <h1>${e.name}<span class="pill s-${e.status}">${statusLabels()[e.status]}</span></h1>
        <cadence-button variant="secondary" @click=${() => this.openEdit()}>${t('events.detail.editDetails')}</cadence-button>
      </div>
      <p class="sub">${e.type_name ?? t('events.detail.noType')}${e.start_date ? ' · ' + e.start_date : ''}${e.end_date && e.end_date !== e.start_date ? ' → ' + e.end_date : ''}</p>

      <cadence-tabs .tabs=${this.tabs} active=${this.tab} @tab-change=${(ev: CustomEvent) => (this.tab = ev.detail)}></cadence-tabs>

      ${this.tab === 'overview' ? this.renderOverview() : ''}
      ${this.tab === 'details' ? html`<cadence-event-details
          .eventId=${this.eventId} .detailsMd=${e.details_md ?? null} .detailsFilename=${e.details_filename ?? null}
          @saved=${(ev: CustomEvent) => { this.ev = { ...this.ev, details_md: ev.detail.details_md, details_filename: ev.detail.details_filename }; }}></cadence-event-details>` : ''}
      ${this.tab === 'participants' ? this.renderParticipants() : ''}
      ${this.tab === 'fees' ? this.renderFees() : ''}
      ${this.tab === 'expenses' ? this.renderExpenses() : ''}
      ${this.tab === 'revenue' ? this.renderRevenue() : ''}
      ${this.tab === 'gallery' ? html`<div class="panel"><cadence-event-gallery .eventId=${this.eventId} .canEdit=${store.hasPermission('events.edit')}></cadence-event-gallery></div>` : ''}
      ${this.tab === 'todos' ? html`<div class="panel"><cadence-event-todos .eventId=${this.eventId}></cadence-event-todos></div>` : ''}
      ${this.tab === 'notes' ? html`<div class="panel"><cadence-event-notes .eventId=${this.eventId}></cadence-event-notes></div>` : ''}
      ${this.tab === 'files' ? html`<div class="panel"><cadence-event-files .eventId=${this.eventId}></cadence-event-files></div>` : ''}

      <cadence-event-form-modal
        ?open=${this.showEdit} heading=${t('events.form.editHeading')} submit-label=${t('common.actions.save')}
        .types=${this.types} .tags=${this.tags} .value=${this.editValue} ?saving=${this.savingEdit}
        @save=${(e: CustomEvent) => this.saveEdit(e.detail)}
        @close=${() => (this.showEdit = false)}></cadence-event-form-modal>
    `;
  }

  private renderOverview() {
    const e = this.ev;
    return html`<div class="panel">
      <div class="grid">
        <div class="field"><div class="k">${t('events.detail.field.capacity')}</div><div class="v">${e.capacity ?? t('events.detail.unlimited')}</div></div>
        <div class="field"><div class="k">${t('events.detail.field.confirmed')}</div><div class="v">${e.confirmed_count}${e.capacity != null ? '/' + e.capacity : ''}</div></div>
        <div class="field"><div class="k">${t('events.detail.field.feeOptions')}</div><div class="v">${this.fees.length || '—'}</div></div>
        <div class="field">
          <div class="k">${t('events.detail.field.status')}</div>
          <cadence-select .value=${e.status}
            .options=${Object.entries(statusLabels()).map(([value, label]) => ({ value, label }))}
            @change=${(ev: CustomEvent) => this.patchEvent({ status: ev.detail })}></cadence-select>
        </div>
        <div class="field"><div class="k">${t('events.detail.field.visibility')}</div><div class="v">${this.visibilityLabel()}</div></div>
      </div>

      <cadence-card heading=${t('events.detail.financialSummary')}>
        ${this.financials?.by_currency?.length
          ? this.financials.by_currency.map((c: any) => html`
            <div class="fin">
              <div><div class="field"><div class="k">${t('events.detail.revenueCur', { currency: c.currency })}</div><div class="amt pos">${money(c.revenue, c.currency)}</div></div></div>
              <div><div class="field"><div class="k">${t('events.detail.expenses')}</div><div class="amt neg">${money(c.expense, c.currency)}</div></div></div>
              <div><div class="field"><div class="k">${t('events.detail.net')}</div><div class="amt ${c.net >= 0 ? 'pos' : 'neg'}">${money(c.net, c.currency)}</div></div></div>
            </div>`)
          : html`<p class="meta">${t('events.detail.noFinancials')}</p>`}
      </cadence-card>

      ${e.description ? html`<cadence-card heading=${t('events.detail.description')}><p style="white-space:pre-wrap;margin:0">${e.description}</p></cadence-card>` : ''}

      ${e.details_md ? html`<details class="md-card">
        <summary>${t('events.detail.detailsDocument')}${e.details_filename ? html` <span class="fn">· ${e.details_filename}</span>` : ''}</summary>
        <cadence-markdown-renderer .markdown=${e.details_md}></cadence-markdown-renderer>
      </details>` : ''}
    </div>`;
    // NB: the Details document is edited in the dedicated Details tab (cadence-event-details);
    // the read-only card above is a quick at-a-glance view on the overview.
  }

  private visibilityLabel() {
    const e = this.ev;
    if (e.visibility !== 'tags') return t('events.detail.allStudents');
    const ids: string[] = e.visibility_tag_ids ?? [];
    if (!ids.length) return t('events.detail.tagsNoneHidden');
    const names = ids.map((id) => this.tags.find((tg) => tg.id === id)?.name ?? '?');
    return names.join(', ');
  }

  private renderParticipants() {
    const existing = new Set(this.participants.map((p) => p.student_id));
    const addable = this.allStudents.filter((s) => !existing.has(s.id));
    return html`<div class="panel">
      <div class="row">
        <cadence-select placeholder=${t('events.detail.addStudentPlaceholder')} .value=${this.addStudentId}
          .options=${addable.map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` }))}
          @change=${(ev: CustomEvent) => (this.addStudentId = ev.detail)}></cadence-select>
        <cadence-button @click=${() => this.addParticipant()}>${t('events.actions.add')}</cadence-button>
      </div>
      ${this.participants.length
        ? this.participants.map((p) => html`
          <div class="item">
            <div>
              <strong>${p.first_name} ${p.last_name}</strong>
              <div class="meta">
                ${partLabels()[p.status] ?? p.status}
                ${p.fee_payment_id
                  ? ` · ${p.fee_name ?? t('events.detail.feeFallback')} ${money(p.fee_amount ?? 0, p.fee_currency)} (${p.payment_status ?? t('events.detail.billed')})`
                  : p.fee_name ? ` · ${t('events.detail.wants', { name: p.fee_name })}` : ''}
                ${p.agreed_at ? html` · <span class="agreed">✓ ${t('events.detail.readAgreed')}</span>` : ''}
              </div>
            </div>
            <div class="acts">
              ${p.status !== 'confirmed' ? html`<cadence-button variant="secondary" @click=${() => this.setStatus(p.student_id, 'confirmed')}>${t('events.actions.confirm')}</cadence-button>` : ''}
              ${p.status !== 'denied' ? html`<cadence-button variant="ghost" @click=${() => this.setStatus(p.student_id, 'denied')}>${t('events.actions.deny')}</cadence-button>` : ''}
              ${p.status === 'confirmed' && !p.fee_payment_id && this.fees.length ? html`
                <cadence-select placeholder=${t('events.detail.feePlaceholder')} .value=${this.billChoice[p.student_id] ?? p.event_fee_id ?? this.fees[0].id}
                  .options=${this.fees.map((fe) => ({ value: fe.id, label: `${fe.name} — ${money(fe.amount, fe.currency)}` }))}
                  @change=${(ev: CustomEvent) => (this.billChoice = { ...this.billChoice, [p.student_id]: ev.detail })}></cadence-select>
                <cadence-button variant="secondary" @click=${() => this.billFee(p.student_id)}>${t('events.actions.bill')}</cadence-button>` : ''}
              <cadence-button variant="danger" @click=${() => this.removeParticipant(p.student_id)}>${t('events.actions.remove')}</cadence-button>
            </div>
          </div>`)
        : html`<cadence-empty-state heading=${t('events.detail.participantsEmpty.heading')} message=${t('events.detail.participantsEmpty.message')}></cadence-empty-state>`}
    </div>`;
  }

  private renderFees() {
    return html`<div class="panel">
      <p class="meta" style="margin-top:0;color:var(--color-text-secondary)">${t('events.detail.feesIntro')}</p>
      <div class="row">
        <cadence-input label=${t('events.detail.feeName')} placeholder=${t('events.detail.feeNamePlaceholder')} .value=${this.feeForm.name} @input=${(e: CustomEvent) => (this.feeForm = { ...this.feeForm, name: e.detail })}></cadence-input>
        <cadence-input label=${t('events.detail.amount')} type="number" .value=${this.feeForm.amount} @input=${(e: CustomEvent) => (this.feeForm = { ...this.feeForm, amount: e.detail })}></cadence-input>
        <cadence-currency-select .value=${this.feeForm.currency} @change=${(e: CustomEvent) => (this.feeForm = { ...this.feeForm, currency: e.detail })}></cadence-currency-select>
        <cadence-button @click=${() => this.addFee()}>${t('events.actions.add')}</cadence-button>
      </div>
      ${this.fees.length
        ? this.fees.map((fe) => html`
          <div class="item">
            <div><strong>${fe.name}</strong></div>
            <div class="acts"><span>${money(fe.amount, fe.currency)}</span><cadence-button variant="danger" @click=${() => this.delFee(fe.id)}>${t('events.actions.delete')}</cadence-button></div>
          </div>`)
        : html`<cadence-empty-state heading=${t('events.detail.feesEmpty.heading')} message=${t('events.detail.feesEmpty.message')}></cadence-empty-state>`}
    </div>`;
  }

  private renderExpenses() {
    return html`<div class="panel">
      <div class="row">
        <cadence-input label=${t('events.detail.description')} .value=${this.expForm.description} @input=${(e: CustomEvent) => (this.expForm = { ...this.expForm, description: e.detail })}></cadence-input>
        <cadence-input label=${t('events.detail.amount')} type="number" .value=${this.expForm.amount} @input=${(e: CustomEvent) => (this.expForm = { ...this.expForm, amount: e.detail })}></cadence-input>
        <cadence-currency-select .value=${this.expForm.currency} @change=${(e: CustomEvent) => (this.expForm = { ...this.expForm, currency: e.detail })}></cadence-currency-select>
        <cadence-input label=${t('events.detail.dateLabel')} type="date" .value=${this.expForm.date} @input=${(e: CustomEvent) => (this.expForm = { ...this.expForm, date: e.detail })}></cadence-input>
        <cadence-button @click=${() => this.addExpense()}>${t('events.actions.add')}</cadence-button>
      </div>
      ${this.expenses.length
        ? this.expenses.map((x) => html`
          <div class="item">
            <div><strong>${x.description}</strong><div class="meta">${x.date}${x.notes ? ' · ' + x.notes : ''}</div></div>
            <div class="acts"><span>${money(x.amount, x.currency)}</span><cadence-button variant="danger" @click=${() => this.delExpense(x.id)}>${t('events.actions.delete')}</cadence-button></div>
          </div>`)
        : html`<cadence-empty-state heading=${t('events.detail.expensesEmpty.heading')} message=${t('events.detail.expensesEmpty.message')}></cadence-empty-state>`}
    </div>`;
  }

  private renderRevenue() {
    return html`<div class="panel">
      <div class="row">
        <cadence-input label=${t('events.detail.description')} .value=${this.revForm.description} @input=${(e: CustomEvent) => (this.revForm = { ...this.revForm, description: e.detail })}></cadence-input>
        <cadence-input label=${t('events.detail.amount')} type="number" .value=${this.revForm.amount} @input=${(e: CustomEvent) => (this.revForm = { ...this.revForm, amount: e.detail })}></cadence-input>
        <cadence-currency-select .value=${this.revForm.currency} @change=${(e: CustomEvent) => (this.revForm = { ...this.revForm, currency: e.detail })}></cadence-currency-select>
        <cadence-input label=${t('events.detail.dateLabel')} type="date" .value=${this.revForm.date} @input=${(e: CustomEvent) => (this.revForm = { ...this.revForm, date: e.detail })}></cadence-input>
        <cadence-button @click=${() => this.addRevenue()}>${t('events.actions.add')}</cadence-button>
      </div>
      ${this.revenue.length
        ? this.revenue.map((x) => html`
          <div class="item">
            <div><strong>${x.description}</strong><div class="meta">${x.date}${x.payment_id ? ' · ' + t('events.detail.fromParticipantFee') : ''}${x.notes ? ' · ' + x.notes : ''}</div></div>
            <div class="acts"><span>${money(x.amount, x.currency)}</span><cadence-button variant="danger" @click=${() => this.delRevenue(x.id)}>${t('events.actions.delete')}</cadence-button></div>
          </div>`)
        : html`<cadence-empty-state heading=${t('events.detail.revenueEmpty.heading')} message=${t('events.detail.revenueEmpty.message')}></cadence-empty-state>`}
    </div>`;
  }

}
